import {
  Dialog,
  Button,
  Typography,
  Box,
  TextField,
  Snackbar,
  Checkbox,
  Select,
  MenuItem,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import React, { useEffect, useMemo, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { ManagementUsersDialogStyle as styles } from "./ManagementUsersDialogStyle";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { pendingShipmentUpdatedFlagState } from "../recoil/atoms/pendingShipmentAtoms";
import { UserListItem } from "./Table/UserListTable";
import { baseSearchOptionsSelector } from "../recoil/selectors/baseSearchOptionsSelector";
import BaseFilterSelect from "./BaseFilter";
import { CheckBox } from "@mui/icons-material";
import SelectBox from "./common/SelectBox";
import WarehouseTable from "./Table/WarehouseTable";
import { warehouseMap } from "./ManagementUsersDialog";

type Props = {
  open: boolean;
  onClose: () => void;
  onUpdate: (updatedUser: UserListItem, baseIds: number[]) => void;
  userInfo: UserListItem;
};

const WarehousesSettingDialog: React.FC<Props> = ({
  open,
  userInfo,
  onClose,
  onUpdate,
}) => {
  const setPendingShipmentUpdateFlag = useSetRecoilState(
    pendingShipmentUpdatedFlagState
  );

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const handleCloseToast = () => setToastOpen(false);

  const [lastName, setLastName] = useState("");
  const [firstName, setFirstrName] = useState("");
  const [mailAddress, setMailAddress] = useState("");
  const [shopName, setShopName] = useState("");
  const [roomId, setRoomId] = useState("");
  const [driverName, setDriverName] = useState("");
  const [memo, setMemo] = useState("");
  const [isHopeWarehuse, setIsHopeWarehouse] = useState(
    userInfo?.isHopeWarehouse ?? false
  );
  const [editUserInfo, setEditUserInfo] = useState<UserListItem>();
  const [baseName, setBaseName] = useState("");
  const [selectedBaseId, setSelectedBaseId] = useState<number | null>(null);
  const [selectedBaseIds, setSelectedBaseIds] = useState<number[]>([]);

  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  const [checkedWarehouseIds, setCheckedWarehouseIds] = useState<Set<string>>(
    new Set()
  );

  const handleCheckChange = (id: string) => {
    setCheckedWarehouseIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleCheckChangeAll = (newCheckedIds: Set<string>) => {
    setCheckedWarehouseIds(newCheckedIds);
  };

  // 拠点データ取得
  const baseSearchOptions = useRecoilValue(baseSearchOptionsSelector);
  const baseOptions = useMemo(() => {
    return (
      baseSearchOptions?.map((base) => ({
        label: `${base.name}`,
        id: base.id!,
      })) ?? []
    );
  }, [baseSearchOptions]);

  const resetFields = () => {
    if (userInfo) {
      setLastName(userInfo.lastName ?? "");
      setFirstrName(userInfo.firstName ?? "");
      setMailAddress(userInfo.mailAddress ?? "");
      setShopName(userInfo.shopName ?? "");
      setRoomId(String(userInfo.roomId ?? ""));
      setDriverName(userInfo.driverName ?? "");
      setMemo(userInfo.memo ?? "");

      // エラー対策: 空配列をデフォルトに
      const base = (baseSearchOptions ?? []).find(
        (b) => b.name === userInfo.base
      );
      setSelectedBaseId(base?.id ?? null);
      setBaseName(userInfo.base ?? "");
      setSelectedBaseIds(userInfo.baseIds ?? []);

      setEditUserInfo(userInfo);
    }
  };

  // 初期値セット
  useEffect(() => {
    resetFields();
    if (!userInfo) return;
    setSelectedBaseIds(userInfo.baseIds ?? []);
    setIsHopeWarehouse(userInfo.isHopeWarehouse ?? false);

    const newSet = new Set<string>();

    Object.entries(warehouseMap).forEach(([id, key]) => {
      if (userInfo[key]) {
        newSet.add(String(id)); // ← Set<string> に入れるので文字列化
      }
    });

    setCheckedWarehouseIds(newSet);
  }, [userInfo]);

  useEffect(() => {}, []);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
        <Dialog
          open={open}
          onClose={(event, reason) => {
            if (reason !== "backdropClick") onClose();
          }}
          disableEnforceFocus
          maxWidth={false}
          fullWidth={false}
          PaperProps={{
            sx: {
              display: "flex",
              width: "100%",
              height: "660px",
              maxWidth: "none",
              maxHeight: "none",
              overflowY: "auto",
            },
          }}
        >
          <Box sx={styles.header}>
            <Typography sx={styles.headerText}>倉庫一覧</Typography>
            <Button
              onClick={() => {
                onClose();
              }}
              sx={styles.closeButton}
            >
              ×
            </Button>
          </Box>

          <WarehouseTable
            checkedIds={checkedWarehouseIds}
            onCheckedChange={handleCheckChange}
            onCheckedChangeAll={handleCheckChangeAll}
          />

          <Button
            onClick={() => {
              const selectedIds: number[] = [];
              checkedWarehouseIds.forEach((id) => {
                selectedIds.push(Number(id));
              });

              onUpdate(editUserInfo!, selectedIds);
            }}
            variant="contained"
            sx={styles.floatingRegisterButton}
          >
            更新
          </Button>

          <Snackbar
            open={toastOpen}
            autoHideDuration={4000}
            onClose={handleCloseToast}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert
              onClose={handleCloseToast}
              severity={toastSeverity}
              sx={{ width: "100%" }}
              elevation={6}
              variant="filled"
            >
              {toastMessage}
            </MuiAlert>
          </Snackbar>
        </Dialog>
      </LocalizationProvider>
    </>
  );
};

export default WarehousesSettingDialog;
